/* eslint-disable jsx-a11y/anchor-has-content */
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import cssClass from 'classnames';

import { LoadingSkeleton as Skeleton } from '../../atoms';
import { usePageContext, pageStoreComponentID } from '../../organisms/PageContext';
import { BrandedHotelHeader } from './BrandedHotelHeader';
import gqlQuery from './queries/hotelHeaderDetail.graphql';
import gqlMutation from './mutations/savePropertiesToProfile.graphql';
import {
  getSavePropertiesData,
  parseHotelHeaderData,
  getPropertyLogo,
  recentlyViewed,
  fetchCustomerHeaderData,
} from './helper';
import { SessionDataMap } from './HotelHeaderV2.schema';
import { HotelHeaderProps, ComponentProps } from './HotelHeaderV2.types';
import { StyledComp, StyledLoadingSection } from './HotelHeaderV2.styles';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';

import {
  parseContactDetails,
  isMobileViewPort,
  getWindowSession,
  useQueryFetch,
  useMutationFetch,
  transformResponse,
  gql,
  addSubDirectoryPrefix,
  getHotelDetailURL,
  updateBookDTTPrintModalUXLStatus,
  canUseDOM,
  useLogoAutomation,
} from '../../utils';
import { constants, GQL_OPT_NAME_DTT, URL_CONSTANTS, LOADER_CONFIGS, ENV_CONSTANTS } from '../../constants';
import { LogoContainer } from '@marriott/mi-ui-library';

const { HotelHeaderImageLoadingConfig, HotelHeaderDetailsLoadingConfig } = LOADER_CONFIGS;
export const HotelHeaderLoader = (props: { inModal: boolean }) => {
  return props.inModal ? (
    <div data-component-name="o-book-HotelHeader-Loading">
      <Skeleton count={1} height={isMobileViewPort() ? 152 : 126} />
    </div>
  ) : (
    <StyledLoadingSection data-component-name="o-HotelHeaderV2" className="d-flex align-items-center container">
      <div className="mr-3 icon-loader">
        <Skeleton customSkeletonConfig={HotelHeaderImageLoadingConfig} style={{ borderRadius: '25px' }} />
      </div>
      <div className="py-5">
        <Skeleton customSkeletonConfig={HotelHeaderDetailsLoadingConfig} style={{ borderRadius: '25px' }} />
      </div>
    </StyledLoadingSection>
  );
};

export const HotelHeader: FC<ComponentProps> = props => {
  const initialState = {
    country: '',
    city: '',
    phone: '',
    postalCode: '',
    addressLine1: '',
    addressLine2: '',
    addressLine3: '',
    stateProvince: '',
    brandCode: '',
    hotelName: '',
    seoNickname: '',
    userData: {},
    imageUrl: '',
  };

  const {
    isInEditor,
    inModal,
    reviews,
    componentId,
    fileReference,
    hotelDetailsLabel,
    pageType,
    savedLabel,
    saveLabel,
    phoneText,
    starsOutOfText,
    inCancelModal,
    reviewUrl,
    overridePropertyCountry,
    overridePropertyCountryText,
    disableHotelHeaderAndReviewLinks,
    disableHotelReviewLinks,
    reviewsLabel,
    isAccountPage = false,
    disableReviews = false,
    propertyCodes,
  } = props;
  const {
    REMEMBERED_STATE,
    RLM_PAGE,
    ACCESS_TYPE,
    REMEMBER_ME_STATE,
    RLM_AND_POSTBOOKING_PAGES,
    BRAND_ICON_HH_CONFIG,
    BULGARI_BRAND_CODE,
  } = constants;
  const { PROPERTY_URL, PROPERTY_LOCATION_URL, HOTEL_MAP_POST_URL } = URL_CONSTANTS;
  const [hotelHeaderData, setHotelHeaderData] = useState<HotelHeaderProps>(initialState);
  const [aggregatedData, setAggregatedData] = useState<Record<string, string | number | boolean> | undefined>({});
  const [uniqueHotelLogo, setUniqueHotelLogo] = useState('');
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const GET_HOTEL_HEADER = gql`
    ${gqlQuery}
  `;
  const { hotelHeader } = GQL_OPT_NAME_DTT;
  const UPDATE_CUSTOMER_MUTATION = gql`
    ${gqlMutation}
  `;
  const mutationOperationName = GQL_OPT_NAME_DTT.customerSavedProperties;

  const { updateComponentState = () => '' } = usePageContext(state => state.hotelHeader);

  const sessionObject = getWindowSession();
  const isAuth = sessionObject?.authenticated;
  const isRlmPage = pageType === RLM_PAGE;
  const isRlmOrPostBookingPage = (!!pageType && RLM_AND_POSTBOOKING_PAGES.includes(pageType)) || isAccountPage;
  const envVarsObject = useClientEnvVarsStore(state => state.envVarsObject);
  const imageDomain = envVarsObject?.[ENV_CONSTANTS.uxlImageCacheDomain] ?? URL_CONSTANTS.CACHE_MARRIOTT_URL;

  // Feature Flag to enable DAC logo otherwise CTS logo and aem brand logo will work
  let isDACLogo = envVarsObject?.[ENV_CONSTANTS.dacLogoEnable] === constants.TRUE;
  if (!isDACLogo) {
    // TODO: to test with query param same env variable functionality
    isDACLogo = canUseDOM && window?.location?.search?.includes('daclogo=true');
  }

  const updateHotelHeaderContext = useCallback(
    (dataObj: Record<string, string | number | boolean>) => {
      if (isRlmPage) {
        sessionStorage.removeItem('insuranceData');
        sessionStorage.removeItem('insurancePurchased');
        sessionStorage.removeItem('insuranceShown');
      }
      updateComponentState({
        componentName: pageStoreComponentID.HOTEL_HEADER,
        newState: {
          data: dataObj,
        },
      });
    },
    [updateComponentState]
  );

  const { tripsXRequestedByHeader, locale, consumerID, userState, isMax } = transformResponse(
    sessionObject,
    SessionDataMap
  );

  let { propertyId = '' } = transformResponse(sessionObject, SessionDataMap);

  const urlParams = new URLSearchParams(window.location.search);
  const propertyCode = urlParams?.get('propertyCode') || '';
  if (propertyCode) {
    propertyId = propertyCode;
  }

  const isRememberedUser = userState?.toLowerCase() === REMEMBERED_STATE && consumerID;

  const [getHotelHeaderData, { loading }] = useQueryFetch(
    GET_HOTEL_HEADER,
    hotelHeader,
    tripsXRequestedByHeader,
    false,
    undefined,
    {},
    () => updateBookDTTPrintModalUXLStatus(hotelHeader),
    () => updateBookDTTPrintModalUXLStatus(hotelHeader)
  );
  const [savePropertyToProfile] = useMutationFetch(
    UPDATE_CUSTOMER_MUTATION,
    mutationOperationName,
    '',
    isRememberedUser ? { [ACCESS_TYPE]: REMEMBER_ME_STATE } : {}
  );

  let isLoading = loading || !aggregatedData || !hotelHeaderData?.brandCode;

  if (isInEditor) {
    isLoading = false;
  }

  const isSession = sessionObject?.cacheData?.data?.AriesReservation;
  const showSaveProperty = isAuth || isRememberedUser;
  const containerClassName = cssClass({ hide: isLoading, 'top-container': inCancelModal });

  const {
    brandCode,
    hotelName,
    city,
    currency,
    seoNickname,
    phone,
    stateProvince,
    country,
    postalCode,
    addressLine1,
    addressLine2,
    addressLine3,
    latitude,
    longitude,
    stars,
    hotelReviews,
    isJPLocale,
    suppressReviews,
    showReviews,
    userData,
    imageUrl,
    hotelLogoImg,
  } = hotelHeaderData;

  const brandSize = BRAND_ICON_HH_CONFIG[brandCode?.toLowerCase() as keyof typeof BRAND_ICON_HH_CONFIG];

  const isBulgariProperty = brandCode?.toUpperCase() === BULGARI_BRAND_CODE;

  const hotelAddress = parseContactDetails({
    city,
    stateProvince,
    country,
    postalCode,
    addressLine1,
    addressLine2,
    addressLine3,
    isJPLocale,
    inModal,
    overridePropertyCountry,
    overridePropertyCountryText,
    locale,
  });

  useEffect(() => {
    if (isSession) {
      fetchCompleteData();
    }
  }, [isSession, propertyId, getHotelHeaderData, isAuth, isRememberedUser, consumerID, propertyCode]);

  useEffect(() => {
    if (aggregatedData?.catalog) {
      parseHotelHeaderData({ data: aggregatedData }, setHotelHeaderData, locale, imageDomain);
      updateHotelHeaderContext(aggregatedData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aggregatedData]);

  //Method to update unique hotel logo in Property Header
  useEffect(() => {
    if (propertyId && !inModal && !isDACLogo) {
      getPropertyLogo(propertyId, setUniqueHotelLogo, locale);
    }
  }, [propertyId, inModal, getPropertyLogo, isDACLogo]);

  const sessionData = transformResponse(sessionObject, SessionDataMap);

  const recentlyViewData = {
    name: hotelName || '',
    address: hotelAddress || '',
    marshaCode: sessionData?.propertyId || '',
    imageUrl: imageUrl || '',
    latitude: latitude || '',
    longitude: longitude || '',
    marshaBrandCode: brandCode || '',
    numberOfReviewsCount: hotelReviews?.['count'] || '',
    starsCount: stars?.['count'] || '',
    postalAddress: postalCode || '',
    state: stateProvince || '',
    country: country || '',
    createdAt: new Date().toISOString(),
  };

  useEffect(() => {
    if (isRlmPage) {
      recentlyViewed(recentlyViewData, sessionData);
    }
  }, [recentlyViewData]);

  async function fetchCompleteData() {
    try {
      const promises = [
        getHotelHeaderData({
          variables: {
            propertyId: propertyId || propertyCode,
            customerId: consumerID ?? '',
          },
        }),
      ];

      if (isRlmOrPostBookingPage && (isAuth || isRememberedUser)) {
        promises.push(fetchCustomerHeaderData(consumerID));
      }

      const [hotelHeaderResponse, customerHeaderResponse] = await Promise.all(promises);

      const { data: hotelData } = hotelHeaderResponse;
      const customerData = customerHeaderResponse;

      setAggregatedData({
        ...hotelData,
        customer: customerData ?? {},
      });
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  }
  const handleSaveHotel = async (
    propertyList: { [id: string]: string }[],
    setHotelSave: React.Dispatch<React.SetStateAction<{ addClass: string; showSavedLabel: boolean }>>,
    hotelSave: Record<string, string | boolean>,
    saveProperty?: boolean
  ) => {
    if (userData?.savedProperties && propertyList?.length <= 20) {
      const { triggerSave, index, updatedPropertyList } = getSavePropertiesData(
        propertyList,
        propertyId,
        isFirstLoad,
        setIsFirstLoad,
        saveProperty
      );
      if (triggerSave) {
        let status = constants.ERROR;
        const variables = {
          input: {
            id: consumerID,
            savedProperties: updatedPropertyList,
            revisionToken: userData?.revisionToken,
          },
        };
        status = await savePropertyToProfile({
          variables: variables,
        })
          .then(result => {
            return result.data.updateCustomerSavedProperties.status[0].code;
          })
          .catch(err => console.log(err, 'Error while updating properties'));
        if (status?.toLowerCase() === constants.SUCCESS) {
          const refetchedCustomerData = await fetchCustomerHeaderData(consumerID);

          setAggregatedData(prev => ({
            ...prev,
            customer: refetchedCustomerData ?? {},
          }));

          if (index > -1) {
            setHotelSave({ ...hotelSave, addClass: 'icon-heart-outline', showSavedLabel: false });
          } else {
            setHotelSave({ ...hotelSave, addClass: 'icon-heart-fill', showSavedLabel: true });
          }
        }
      }
    }
  };

  const brandedHeaderData = {
    componentId,
    seoNickname,
    hotelName,
    fileReference,
    latitude,
    longitude,
    currency,
    hotelAddress,
    phone,
    showReviews,
    suppressReviews,
    stars,
    hotelReviews,
    reviews,
    hotelDetailsLabel,
    savedLabel,
    saveLabel,
    brandCode,
    propertyId,
    userData,
    showSaveProperty,
    consumerID,
    imageUrl,
    phoneText,
    starsOutOfText,
    reviewUrl,
    isRlmOrPostBookingPage,
    isMax,
    disableHotelHeaderAndReviewLinks,
    disableHotelReviewLinks,
    locale,
    reviewsLabel,
    disableReviews,
    city,
    dacHotelLogo: hotelLogoImg,
    isDACLogo,
    propertyCodes,
  };

  let hotelDetailUrl = useMemo(
    () =>
      getHotelDetailURL({
        brandCode,
        locale,
        seoNickname,
        marshaCode: propertyId,
        location: city?.toLocaleLowerCase(),
        bulgariPropertyCodes: propertyCodes?.bulgariPropertyCodes,
      }),
    [brandCode, locale, propertyId, city]
  );

  let hotelLocationUrl = addSubDirectoryPrefix(
    `${PROPERTY_LOCATION_URL}${seoNickname}?${HOTEL_MAP_POST_URL}${seoNickname}`
  );

  if (isBulgariProperty) {
    hotelLocationUrl = hotelDetailUrl + '/location';
  }
  hotelDetailUrl = hotelDetailUrl || addSubDirectoryPrefix(`${PROPERTY_URL}${seoNickname}`);

  hotelDetailUrl = disableHotelHeaderAndReviewLinks ? '' : hotelDetailUrl;

  const isLogoFlagEnabled = useLogoAutomation();

  return (
    <>
      {isLoading && <HotelHeaderLoader inModal={Boolean(inModal)} />}
      {inModal ? (
        <StyledComp
          data-component-name="o-book-HotelHeader"
          className={containerClassName}
          brandCode={brandCode}
          brandSize={brandSize}
        >
          {isLogoFlagEnabled ? (
            <div className="hotelLogo">
              <LogoContainer
                brand={brandCode || ''}
                width={100}
                height={66.67}
                aspectRatio={'3:2'}
                alignment={'Centered'}
                isLink={false}
                color={'background-aware'}
              />
            </div>
          ) : (
            <span className={`t-brand-logo brand-logo-${brandCode} hotelLogo t-brand-logo-s`}></span>
          )}
          <div className="headerContent">
            <a
              href={hotelDetailUrl}
              aria-label={hotelDetailUrl}
              className={disableHotelHeaderAndReviewLinks ? 'disableLinks' : ''}
            >
              <span className="title">{hotelName}</span>
            </a>
            {/*  Show hotel header data only when hotel header data is received */}
            {hotelName && (
              <div className="addressWrapper">
                <a
                  className="icon-location icon-margin"
                  href={hotelLocationUrl}
                  aria-label={hotelAddress}
                  {...(isBulgariProperty && { target: '_blank' })}
                />
                <p className="address">{hotelAddress}</p>
              </div>
            )}
          </div>
        </StyledComp>
      ) : (
        !isLoading && (
          <BrandedHotelHeader
            {...brandedHeaderData}
            enableHotelDetailsLink={pageType === 'none'}
            uniqueHotelLogo={uniqueHotelLogo}
            isLoading={isLoading}
            handleClick={handleSaveHotel}
          />
        )
      )}
    </>
  );
};
export default React.memo(HotelHeader);
